import { NgModule, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { IonicModule } from '@ionic/angular';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { AppTranslateService } from './services/translate.service';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Router, RouterModule } from '@angular/router';
// Spotify OAuth interceptor — attaches Bearer token to api.spotify.com requests
import { SpotifyAuthInterceptor } from './interceptors/spotify-auth.interceptor';

import { provideFirebaseApp, initializeApp as initializeFirebaseApp, getApp } from '@angular/fire/app';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { provideAuth } from '@angular/fire/auth';
import { provideFunctions, getFunctions, connectFunctionsEmulator } from '@angular/fire/functions';

import { initializeAuth, indexedDBLocalPersistence, getAuth as getWebAuth, browserPopupRedirectResolver } from 'firebase/auth';
import { environment } from '../environments/environment';
import { setLogLevel, LogLevel } from '@angular/fire';
import { Capacitor } from '@capacitor/core';

// Removed hammerjs import - switched to touch-based swiping
// import 'hammerjs';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    IonicModule.forRoot(),
    AppRoutingModule,
    RouterModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: { provide: TranslateLoader, useFactory: HttpLoaderFactory, deps: [HttpClient] }
    }),
  ],
  providers: [
    provideFirebaseApp(() => initializeFirebaseApp(environment.firebaseConfig)),

    // Ensure Firestore is the normal web SDK instance
    provideFirestore(() => getFirestore()),

    provideFunctions(() => {
      const functions = getFunctions();
      if (environment.useEmulators) {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      return functions;
    }),

    // IMPORTANT: Initialize Auth with IndexedDB persistence for WKWebView
    provideAuth(() => {
      const app = getApp();
      try {
        // On native (Capacitor) platforms we should not attach the browser popup resolver
        // because WKWebView/Cordova/Capacitor environments handle native SSO differently.
        // For web, include the browser popup resolver so popup-based SSO works.
        // Note: Keep indexedDB persistence for WKWebView compatibility.
        if (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
          return initializeAuth(app, { persistence: indexedDBLocalPersistence });
        }
        return initializeAuth(app, { persistence: indexedDBLocalPersistence, popupRedirectResolver: browserPopupRedirectResolver });
      } catch {
        // If already initialized (e.g., HMR), fall back to the default getter
        return getWebAuth(app);
      }
    }),
    AppTranslateService,
    // Spotify OAuth: attach Bearer token to https://api.spotify.com requests and
    // handle proactive refresh + 401 retry.  multi:true keeps other interceptors intact.
    { provide: HTTP_INTERCEPTORS, useClass: SpotifyAuthInterceptor, multi: true },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {
    setLogLevel(LogLevel.SILENT);
  }
}
